import PageBanner from "@/components/feature/PageBanner";
import AboutTabs from "./components/AboutTabs";
import { useApi } from "@/hooks/useApi";
import type { ClubInfo, Officer } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { mediaUrl } from "@/lib/media";

export default function Officers() {
  const { data, loading, error, reload } = useApi<ClubInfo>("club.php");
  const officers = data?.officers ?? [];

  return (
    <>
      <PageBanner
        title="임원"
        description="대전민들레로타리클럽을 이끌어가는 임원진을 소개합니다."
        breadcrumb={[{ label: "클럽소개", to: "/about/greeting" }, { label: "임원" }]}
      />
      <AboutTabs />

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="임원 정보를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                다시 시도
              </button>
            }
          />
        ) : officers.length === 0 ? (
          <EmptyState icon="ri-user-star-line" title="등록된 임원 정보가 없습니다" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {officers.map((o, i) => (
              <OfficerCard key={`${o.name}-${i}`} officer={o} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function OfficerCard({ officer }: { officer: Officer }) {
  const initial = (officer.name || "?").trim().slice(0, 1);

  return (
    <article className="rounded-xl border border-background-200 bg-background-50 p-4 text-center">
      <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-background-100 border border-background-200 flex items-center justify-center">
        {officer.photo ? (
          <img
            src={mediaUrl(officer.photo)}
            alt={`${officer.name} 임원 사진`}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <span className="font-heading text-2xl font-bold text-primary-600">
            {initial}
          </span>
        )}
      </div>
      <h3 className="mt-4 font-heading font-bold text-foreground-950">
        {officer.name}
        {officer.aho && (
          <span className="ml-1 text-sm font-medium text-foreground-500">
            ({officer.aho})
          </span>
        )}
      </h3>
      {officer.role && (
        <span className="mt-2 inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
          {officer.role}
        </span>
      )}
    </article>
  );
}